import InformationBanner from './InformationBanner';
import { EmploymentDateInputForm, EmploymentSalaryInputForm } from './EmploymentInputs';
import { useForm } from 'react-hook-form';
import Icon from '@mdi/react';
import { mdiAlertCircleOutline, mdiFileDocumentEditOutline } from '@mdi/js';
import { useGetEmploymentTypes } from '@my-pages/features/employment/hooks/queries.ts';
import { IEmploymentInfo } from '@shared/integrations/taf-api/v2/models';
import { formatDate, getStartOfNextMonth } from '@shared/utils/dateUtils';
import EmployerArea from '@my-pages/features/employment/components/EmployerArea.tsx';
import useEmploymentFormActions from '@my-pages/features/employment/hooks/useEmploymentFormActions.ts';
import { useGetPerson } from '@my-pages/hooks/useGetPerson.ts';
import {EmploymentUpdateStatus} from "@my-pages/features/employment/types";

interface INewEmploymentFormHandler {
    employment: IEmploymentInfo;
    closeForm: () => void;
    setDisplayMessage: (value: EmploymentUpdateStatus | null) => void;
}

export const NewEmploymentFormHandler = ({ closeForm, setDisplayMessage }: INewEmploymentFormHandler) => {
    const employmentTypes = useGetEmploymentTypes();
    const person = useGetPerson();
    const { updateNewEmployment } = useEmploymentFormActions();
    const {
        handleSubmit,
        register,
        control,
        formState: { isValid, errors },
    } = useForm({ mode: 'onChange' });

    const submit = async (submitData: any) => {
        const submitDataWithDefaultData = {
            ...submitData,
            salary: Number(submitData.salary),
            memberId: person.id,
            employmentArea: EmployerArea(submitData),
            selectedMonthChangeEmployment: formatDate(submitData.selectedMonthChangeEmployment),
            // @ts-ignore
            employementStatusId: employmentTypes[employmentTypes.active],
            employmentForm: 'Anställd',
        };

        const result = await updateNewEmployment(submitDataWithDefaultData);
        setDisplayMessage(result);
        closeForm();
    };

    return (
        <form onSubmit={handleSubmit(submit)}>
            <h3 className='d-flex'>
                <Icon
                    path={mdiFileDocumentEditOutline}
                    className='mdi mdi-36 mr-2 mt-n2'
                />
                <span>Ändra inkomst/​anställning</span>
            </h3>
            <p className='required-info'>* Obligatoriska uppgifter</p>
            <EmploymentSalaryInputForm
                name='salary'
                register={{ ...register('salary', { required: true }) }}
            />
            <h4 className='mt-5'>Arbetsgivare</h4>
            <div className='form-group row'>
                <label
                    htmlFor='employmentCompanyName'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Företagets namn
                </label>
                <div className='col-md-9'>
                    <input
                        type='text'
                        id='employmentCompanyName'
                        {...register('employmentName', { required: true })}
                        className='form-control'
                        autoComplete='employmentCompanyName'
                    />
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='organizationNumber'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Organisations­nummer
                </label>
                <div className='col-md-9'>
                    <input
                        type='text'
                        id='organizationNumber'
                        {...register('employmentOrg', {
                            required: true,
                            validate: (value) => value > 0,
                        })}
                        className='form-control'
                        autoComplete='organizationNumber'
                    />
                    {errors.employmentOrg && (
                        <div className='validation-message slide-up-fade-in'>
                            <Icon
                                path={mdiAlertCircleOutline}
                                className='mdi mdi-18 mr-1'
                            />
                            <span className='validation-text'>Endast siffror är tillåtna</span>
                        </div>
                    )}
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='employmentWorkplaceAddress'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Arbetsplatsens adress
                </label>
                <div className='col-md-9'>
                    <input
                        type='text'
                        id='employmentWorkplaceAddress'
                        {...register('employerStreetAdress', { required: true })}
                        className='form-control'
                        autoComplete='employmentWorkplaceAddress'
                    />
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='workplacePostCode'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Postnummer
                </label>
                <div className='col-md-9'>
                    <input
                        type='number'
                        id='workplacePostCode'
                        {...register('employerZipcode', { required: true })}
                        className='form-control'
                        autoComplete='workplacePostCode'
                    />
                </div>
            </div>
            <div className='form-group row'>
                <label
                    htmlFor='employmentWorkplaceCity'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Ort
                </label>
                <div className='col-md-9'>
                    <input
                        type='text'
                        id='employmentWorkplaceCity'
                        {...register('employerCity', { required: true })}
                        className='form-control'
                        autoComplete='employmentWorkplaceCity'
                    />
                </div>
            </div>
            <h4 className='mt-5'>Vad kommer du att arbeta med?</h4>
            <div className='form-group row'>
                <label
                    htmlFor='employmentJobTitle'
                    className='col-md-3 col-form-label label-sm label-required'
                >
                    Yrke/titel
                </label>
                <div className='col-md-9'>
                    <input
                        type='text'
                        id='employmentJobTitle'
                        {...register('employmentTitle', { required: true })}
                        className='form-control'
                        autoComplete='employmentJobTitle'
                    />
                </div>
            </div>
            <h4 className='mt-5'>Från vilken månad gäller dessa uppgifter?</h4>
            <EmploymentDateInputForm
                control={control}
                rules={{ required: true }}
                minDate={getStartOfNextMonth()}
                name='selectedMonthChangeEmployment'
            />
            <InformationBanner />
            <div className='d-flex justify-content-sm-end mt-5 mb-4'>
                <button
                    className='btn btn-light btn-lg mr-3'
                    type='button'
                    onClick={() => closeForm()}
                >
                    Avbryt
                </button>
                <button
                    className='btn btn-primary btn-lg'
                    disabled={!isValid}
                >
                    Spara
                </button>
            </div>
        </form>
    );
};
